import React, { useState, useEffect } from 'react';
import { FaEye, FaTimes } from 'react-icons/fa';
import { createColumnHelper } from "@tanstack/react-table";
import TanStackTable from './TanStackTable';
import Chip from './Chip';
import { useGetCouponLogs } from '../queries/campaign';

const CouponLogsModal = ({ isOpen, onClose, coupon }) => {
    const [selectedLog, setSelectedLog] = useState(null);
    const columnHelper = createColumnHelper();
    
    // Use React Query hook for fetching coupon logs
    const { 
        data: couponLogs = [], 
        isLoading, 
        error, 
        refetch 
    } = useGetCouponLogs(coupon?._id, coupon?.code, isOpen);

    if (!isOpen) return null;

    const logsColumns = [
        columnHelper.display({
            id: 'srno',
            header: 'Sr No.',
            cell: info => info.row.index + 1,
        }),
        columnHelper.accessor('type', {
            header: 'Type',
            align: 'center',
            cell: info => {
                const type = info.getValue();
                const getTypeColor = (type) => {
                    switch (type?.toLowerCase()) {
                        case 'redemption': return 'blue';
                        case 'error': return 'red';
                        case 'success': return 'green';
                        default: return 'gray';
                    }
                };
                return <Chip value={type || 'N/A'} color={getTypeColor(type)} small />;
            },
        }),
        // columnHelper.accessor('body.userId', {
        //     header: 'User ID',
        //     cell: info => {
        //         const userId = info.getValue();
        //         return (
        //             <div className="font-mono text-sm max-w-xs overflow-hidden text-ellipsis">
        //                 {userId || 'N/A'}
        //             </div>
        //         );
        //     },
        // }),
        // columnHelper.accessor('body.couponCode', {
        //     header: 'Coupon Code',
        //     align: 'center',
        //     cell: info => {
        //         const code = info.getValue();
        //         return (
        //             <div className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
        //                 {code || 'N/A'}
        //             </div>
        //         );
        //     },
        // }),
        // columnHelper.accessor('body.orderId', {
        //     header: 'Order ID',
        //     cell: info => {
        //         const orderId = info.getValue();
        //         return (
        //             <div className="font-mono text-sm max-w-xs overflow-hidden text-ellipsis">
        //                 {orderId || 'N/A'}
        //             </div>
        //         );
        //     },
        // }),
        columnHelper.accessor('status', {
            header: 'Status',
            align: 'center',
            cell: info => {
                const status = info.getValue();
                const success = info.row.original?.success;
                const getStatusColor = (status, success) => {
                    if (status === 200 && success) return 'green';
                    if (status === 409) return 'orange';
                    if (status === 500 || !success) return 'red';
                    return 'gray';
                };
                
                const statusText = success ? 'Success' : (status === 409 ? 'Conflict' : 'Error');
                return <Chip value={`${status} - ${statusText}`} color={getStatusColor(status, success)} small />;
            },
        }),
        columnHelper.accessor('message', {
            header: 'Message',
            cell: info => {
                const message = info.getValue();
                return (
                    <div className="whitespace-normal break-words max-w-xs text-sm">
                        {message || 'N/A'}
                    </div>
                );
            },
        }),
        columnHelper.accessor('createdAt', {
            header: 'Date & Time',
            align: 'center',
            cell: info => {
                const timestamp = info.getValue();
                if (!timestamp) return 'N/A';
                const date = new Date(timestamp);
                return (
                    <div className="whitespace-normal break-words">
                        <div>{date.toLocaleDateString()}</div>
                        <div className="text-sm text-gray-500">
                            {date.toLocaleTimeString([], { hour: 'numeric', minute: 'numeric', second: 'numeric' })}
                        </div>
                    </div>
                );
            },
        }),
        columnHelper.display({
            id: 'actions',
            header: 'Actions',
            align: 'center',
            cell: info => {
                const rowData = info.row.original;
                return (
                    <button
                        onClick={() => setSelectedLog(rowData)}
                        className="rounded p-1.5 bg-[rgba(52,199,89,0.1)] hover:bg-[rgba(52,199,89,0.2)] mx-1 min-h-7 min-w-7"
                        title="View Details"
                    >
                        <FaEye className='fill-primary' />
                    </button>
                );
            },
        }),
    ];

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <div>
                        <h2 className="text-xl font-bold text-gray-900">
                            Coupon Logs
                        </h2>
                        <p className="text-sm text-gray-600 mt-1">
                            Coupon Code: <span className="font-bold">{Array.isArray(coupon?.code) ? coupon.code.join(', ') : (coupon?.code || 'N/A')}</span>
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                        title="Close"
                    >
                        <FaTimes className="text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {/* Coupon Summary */}
                    <div className="bg-gray-50 rounded-lg p-4 mb-6">
                        <h3 className="text-lg font-bold text-gray-900 mb-3">Coupon Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div className="bg-white rounded-lg p-3 shadow">
                                <div className="text-sm text-gray-600">Product</div>
                                <div className="font-bold text-gray-900">
                                    {coupon?.productId?.name || 'N/A'}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow">
                                <div className="text-sm text-gray-600">Campaign</div>
                                <div className="font-bold text-gray-900">
                                    {coupon?.campaignId?.title || 'N/A'}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow">
                                <div className="text-sm text-gray-600">Coupon Code</div>
                                <div className="font-bold text-gray-900">
                                    {coupon?.code || 'N/A'}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow">
                                <div className="text-sm text-gray-600">Discount</div>
                                <div className="font-bold text-gray-900">
                                    {coupon?.typeOfDiscount === 'percentage' 
                                        ? `${coupon?.discountValue || 0}%` 
                                        : `${coupon?.discountValue || 0} Flat`}
                                </div>
                            </div>
                            <div className="bg-white rounded-lg p-3 shadow">
                                <div className="text-sm text-gray-600">Valid Till</div>
                                <div className="font-bold text-gray-900">
                                    {coupon?.campaignId?.endDate 
                                        ? new Date(coupon.campaignId.endDate).toLocaleDateString() 
                                        : 'N/A'}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Logs Table */}
                    <div>
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-bold text-gray-900">Activity Logs</h3>
                            {error && (
                                <button
                                    onClick={() => refetch()}
                                    className="px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    disabled={isLoading}
                                >
                                    Retry
                                </button>
                            )}
                        </div>
                        <div className="bg-white rounded-lg border border-border">
                            {isLoading ? (
                                <div className="p-8 text-center">
                                    <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mb-4"></div>
                                    <div className="text-secondary">Loading coupon logs...</div>
                                </div>
                            ) : error ? (
                                <div className="p-8 text-center">
                                    <div className="text-red-600 mb-2">Failed to load coupon logs</div>
                                    <div className="text-sm text-secondary mb-4">{error?.message || 'Unknown error occurred'}</div>
                                    <button
                                        onClick={() => refetch()}
                                        className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                                    >
                                        Try Again
                                    </button>
                                </div>
                            ) : couponLogs && couponLogs.length > 0 ? (
                                <TanStackTable
                                    data={couponLogs}
                                    columns={logsColumns}
                                    enablePagination={true}
                                />
                            ) : (
                                <div className="p-8 text-center text-secondary">
                                    No activity logs available for this coupon
                                </div>
                            )}
                        </div>
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>

            {/* Log Details Modal */}
            {selectedLog && (
                <LogDetailsModal 
                    log={selectedLog} 
                    onClose={() => setSelectedLog(null)} 
                />
            )}
        </div>
    );
};

// Log Details Modal Component
const LogDetailsModal = ({ log, onClose }) => {
    if (!log) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-60 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-border">
                    <h2 className="text-xl font-bold text-gray-900">
                        Log Details - {log.type}
                    </h2>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 rounded-full transition-colors"
                    >
                        <FaTimes className="text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-6">
                    {/* Basic Information */}
                    <div className="border border-border rounded-lg p-4">
                        <h3 className="text-lg font-bold text-darkGrayBg mb-4">Basic Information</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Log ID</label>
                                <div className="p-3 bg-gray-50 rounded border border-border font-mono text-sm">
                                    {log._id}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                                <div className="p-3 bg-gray-50 rounded border border-border">
                                    {log.type}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Status Code</label>
                                <div className="p-3 bg-gray-50 rounded border border-border">
                                    {log.status}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Success</label>
                                <div className="p-3 bg-gray-50 rounded border border-border">
                                    {log.success ? 'Yes' : 'No'}
                                </div>
                            </div>
                            <div className="md:col-span-2">
                                <label className="block text-sm font-medium text-gray-700 mb-1">Message</label>
                                <div className="p-3 bg-gray-50 rounded border border-border">
                                    {log.message}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Created At</label>
                                <div className="p-3 bg-gray-50 rounded border border-border">
                                    {new Date(log.createdAt).toLocaleString()}
                                </div>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Updated At</label>
                                <div className="p-3 bg-gray-50 rounded border border-border">
                                    {new Date(log.updatedAt).toLocaleString()}
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Request Body */}
                    {log.body && (
                        <div className="border border-border rounded-lg p-4">
                            <h3 className="text-lg font-bold text-darkGrayBg mb-4">Request Body</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Order ID</label>
                                    <div className="p-3 bg-gray-50 rounded border border-border font-mono text-sm">
                                        {log.body.orderId || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Coupon Code</label>
                                    <div className="p-3 bg-gray-50 rounded border border-border font-mono text-sm">
                                        {log.body.couponCode || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">User ID</label>
                                    <div className="p-3 bg-gray-50 rounded border border-border font-mono text-sm">
                                        {log.body.userId || 'N/A'}
                                    </div>
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Product ID</label>
                                    <div className="p-3 bg-gray-50 rounded border border-border font-mono text-sm">
                                        {log.body.productId || 'N/A'}
                                    </div>
                                </div>
                                <div className="md:col-span-2">
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Redemption Time</label>
                                    <div className="p-3 bg-gray-50 rounded border border-border">
                                        {log.body.redemption ? new Date(log.body.redemption).toLocaleString() : 'N/A'}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Request Headers */}
                    {log.headers && (
                        <div className="border border-border rounded-lg p-4">
                            <h3 className="text-lg font-bold text-darkGrayBg mb-4">Request Headers</h3>
                            <div className="bg-darkGrayBg rounded text-primary border border-border p-4">
                                <pre className="text-sm font-mono whitespace-pre-wrap break-all max-h-96 overflow-y-auto">
                                    {JSON.stringify(log.headers, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                    {/* Request Body */}
                    {log.body && (
                        <div className="border border-border rounded-lg p-4">
                            <h3 className="text-lg font-bold text-darkGrayBg mb-4">Request Body</h3>
                            <div className="bg-darkGrayBg rounded text-primary border border-border p-4">
                                <pre className="text-sm font-mono whitespace-pre-wrap break-all max-h-96 overflow-y-auto">
                                    {JSON.stringify(log.body, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t border-border">
                    <button
                        onClick={onClose}
                        className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 transition-colors"
                    >
                        Close
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CouponLogsModal;
